import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchMainCategories } from '../api/client';

/**
 * Same query key as `MainCategoryList`'s own `fetchMainCategories()` call —
 * when both are warm in the TanStack Query cache (e.g. after visiting
 * /discussions and coming back to /), this does not re-fetch.
 */
export function CategoryCards(): React.JSX.Element {
  const mainCategories = useQuery({ queryKey: ['main-categories'], queryFn: fetchMainCategories });

  return (
    <section id="categories" aria-labelledby="category-cards-heading">
      <h2 id="category-cards-heading">Browse by Category</h2>

      {mainCategories.isLoading && <p role="status">Loading categories…</p>}

      {mainCategories.error && (
        <p role="alert" data-testid="categories-error">
          Could not load categories.
        </p>
      )}

      <ul className="category-cards">
        {/* Same pinned "All categories" entry /discussions itself leads with
            (see MainCategoryList) — without it, the home page had no way to
            reach the combined, no-filter view at all. */}
        <li>
          <Link to="/discussions/all" className="category-card" data-testid="category-card-all">
            <strong>All categories</strong>
            <span className="hint">Everything, from every main category, in one list.</span>
          </Link>
        </li>
        {mainCategories.data?.map((mainCategory) => (
          <li key={mainCategory.id}>
            <Link
              to={`/discussions/${encodeURIComponent(mainCategory.id)}`}
              className="category-card"
              data-testid={`category-card-${mainCategory.id}`}
            >
              <strong>{mainCategory.name}</strong>
            </Link>
          </li>
        ))}
      </ul>

      {mainCategories.data && mainCategories.data.length === 0 && (
        <p className="hint">No categories yet — check back soon.</p>
      )}
    </section>
  );
}
